import React from 'react';

const Icon = ({ children }) => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
    >
        {children}
    </svg>
);

const iconByType = {
    call: (
        <Icon>
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.8 19.8 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.08 4.18 2 2 0 0 1 4.06 2h3a2 2 0 0 1 2 1.72c.12.9.3 1.77.54 2.6a2 2 0 0 1-.45 2.11L8.1 9.9a16 16 0 0 0 6 6l1.47-1.05a2 2 0 0 1 2.11-.45c.83.24 1.7.42 2.6.54A2 2 0 0 1 22 16.92z" />
        </Icon>
    ),
    whatsapp: (
        <Icon>
            <path d="M21 11.5a8.5 8.5 0 1 1-16.3-3.6L3 21l3.4-1.7A8.5 8.5 0 0 1 21 11.5z" />
            <path d="M8.5 9.5c.3 1.2 1.8 2.7 3.4 3.4l1.1-.7c.4-.3 1-.2 1.3.2l.7.9c.3.4.2 1-.2 1.3l-1.2.9" />
        </Icon>
    ),
    email: (
        <Icon>
            <rect x="3" y="5" width="18" height="14" rx="2" />
            <path d="M3 7l9 6 9-6" />
        </Icon>
    ),
    meeting: (
        <Icon>
            <circle cx="8" cy="9" r="3" />
            <circle cx="16" cy="9" r="3" />
            <path d="M2 20c1.5-3 5-4 8-4s6.5 1 8 4" />
        </Icon>
    ),
    task: (
        <Icon>
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <path d="M7 12l3 3 7-7" />
        </Icon>
    ),
    site_visit: (
        <Icon>
            <path d="M3 10.5L12 4l9 6.5" />
            <path d="M5 10.5V20h14v-9.5" />
            <path d="M9 20v-6h6v6" />
        </Icon>
    ),
    note: (
        <Icon>
            <path d="M4 4h12l4 4v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" />
            <path d="M14 4v4h4" />
        </Icon>
    ),
};

// Type colors
const typeColors = {
    call: '#2563eb',
    whatsapp: '#16a34a',
    email: '#7c3aed',
    meeting: '#059669',
    task: '#d97706',
    site_visit: '#f97316',
    note: '#6b7280',
};

const getEpoch = (value) => {
    if (!value && value !== 0) return null;
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed.getTime();
};

const inferKind = (activity) => {
    if (activity?.kind === 'followup') return 'scheduled';
    if (activity?.kind) return activity.kind;
    if (activity?.dueAt || activity?.dueDate || activity?.dueTime) return 'scheduled';
    return 'log';
};

const formatChannelLabel = (value) => {
    if (!value) return 'Activity';
    const label = String(value).replace(/_/g, ' ').toLowerCase();
    return label.replace(/(^|\s)\S/g, (match) => match.toUpperCase());
};

const resolveChannelKey = (activity) => {
    if (!activity) return 'other';
    if (activity.category === 'site_visit') return 'site_visit';
    const raw = activity.channel || activity.category || activity.type;
    if (!raw) return 'other';
    const normalized = String(raw).toLowerCase();
    if (normalized.includes('whatsapp')) return 'whatsapp';
    if (normalized.includes('email')) return 'email';
    if (normalized.includes('call') || normalized.includes('phone')) return 'call';
    if (normalized.includes('site')) return 'site_visit';
    if (normalized.includes('meet')) return 'meeting';
    return 'other';
};

const resolveFollowupLabel = (activity) => {
    const label = activity?.meta?.followupTypeLabel || activity?.meta?.followupType;
    if (!label) return '';
    return String(label);
};

const resolveTimeKey = (activity) => {
    const kind = inferKind(activity);
    if (kind === 'log') {
        return getEpoch(activity?.occurredAt)
            ?? getEpoch(activity?.createdAt)
            ?? Date.now();
    }
    if (activity?.status === 'completed' || activity?.completed) {
        return getEpoch(activity?.completedAt)
            ?? getEpoch(activity?.updatedAt)
            ?? getEpoch(activity?.createdAt)
            ?? Date.now();
    }
    return getEpoch(activity?.dueAt)
        ?? getEpoch(activity?.dueDate)
        ?? getEpoch(activity?.createdAt)
        ?? Date.now();
};

// Format relative time
function formatRelativeTime(value) {
    const epoch = getEpoch(value);
    if (!epoch) return '';

    const date = new Date(epoch);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;

    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
}

const outcomeOptions = [
    { value: '', label: 'No outcome' },
    { value: 'no_answer', label: 'No Answer' },
    { value: 'interested', label: 'Interested' },
    { value: 'not_interested', label: 'Not Interested' },
    { value: 'callback', label: 'Callback' },
    { value: 'site_visit_booked', label: 'Site Visit Booked' },
    { value: 'deal_closed', label: 'Deal Closed' },
    { value: 'other', label: 'Other' },
];

function ActivityTimeline({ activities, onComplete, onEdit, onPrimaryAction, onReschedule, onOutcomeChange, lead }) {
    if (!activities || activities.length === 0) {
        return (
            <div className="crm-empty-state">
                <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M12 6v6l4 2" />
                </svg>
                <h3>No activities yet</h3>
                <p>Activities like calls, emails, and meetings will appear here.</p>
            </div>
        );
    }

    const sorted = [...activities].sort((a, b) => resolveTimeKey(b) - resolveTimeKey(a));

    return (
        <div className="crm-timeline">
            {sorted.map((activity) => {
                const type = activity.type || 'note';
                const icon = iconByType[type] || iconByType.note;
                const color = typeColors[type] || '#6b7280';
                const kind = inferKind(activity);
                const isCompleted = kind === 'scheduled' && (activity.completed || activity.status === 'completed');
                const isPending = kind === 'scheduled' && (activity.status === 'scheduled' || activity.status === 'pending' || (!activity.completed && activity.status !== 'completed'));
                const timeKey = resolveTimeKey(activity);
                const dueEpoch = getEpoch(activity?.dueAt) ?? getEpoch(activity?.dueDate);
                const isOverdue = kind === 'scheduled' && !isCompleted && dueEpoch !== null && dueEpoch < Date.now();
                const channelKey = resolveChannelKey(activity);
                const channelLabel = formatChannelLabel(channelKey);
                const followupLabel = resolveFollowupLabel(activity);
                const canEmail = Boolean(lead?.email);
                const canPhone = Boolean(lead?.phone || lead?.mobile);
                const primaryDisabled = (channelKey === 'email' && !canEmail)
                    || ((channelKey === 'call' || channelKey === 'whatsapp') && !canPhone);
                const primaryLabel = channelKey === 'email'
                    ? 'Send Email'
                    : channelKey === 'whatsapp'
                        ? 'Open WhatsApp'
                        : channelKey === 'call'
                            ? 'Call Now'
                            : channelKey === 'site_visit' || channelKey === 'meeting'
                                ? 'Open Map'
                                : 'Open';
                return (
                    <div key={activity.id} className="crm-timeline-item">
                        <div
                            className={`crm-timeline-dot ${isCompleted ? 'completed' : 'pending'} ${kind === 'log' ? 'log' : ''}`}
                            style={{ background: kind === 'log' ? 'var(--crm-gray-400)' : isCompleted ? 'var(--crm-success)' : color }}
                        />

                        <div className={`crm-timeline-content ${isOverdue ? 'overdue' : ''}`}>
                            <div className="crm-timeline-header">
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span className="crm-activity-icon" style={{ color }}>{icon}</span>
                                    <span className="crm-timeline-title">
                                        {kind === 'log'
                                            ? `Logged — ${channelLabel}`
                                            : isCompleted
                                                ? (channelKey === 'email'
                                                    ? 'Email Sent'
                                                    : channelKey === 'whatsapp'
                                                        ? 'WhatsApp Sent'
                                                        : channelKey === 'call'
                                                            ? 'Call Done'
                                                            : channelKey === 'site_visit' || channelKey === 'meeting'
                                                                ? 'Site Visit Done'
                                                                : `${channelLabel} Completed`)
                                                : `Scheduled — ${channelLabel}`}
                                    </span>
                                    {isOverdue && (
                                        <span className="crm-badge crm-badge-overdue">Overdue</span>
                                    )}
                                    {isCompleted && (
                                        <span
                                            className="crm-badge"
                                            style={{ background: 'var(--crm-success-light)', color: 'var(--crm-success)' }}
                                        >
                                            Completed
                                        </span>
                                    )}
                                </div>
                                <span className="crm-timeline-time">
                                    {formatRelativeTime(timeKey)}
                                </span>
                            </div>

                            {activity.description && (
                                <div className="crm-timeline-body">
                                    {activity.description}
                                </div>
                            )}

                            {activity.notes && !activity.description && (
                                <div className="crm-timeline-body">
                                    {activity.notes}
                                </div>
                            )}

                            {kind === 'scheduled' && followupLabel && (
                                <div className="crm-timeline-body" style={{ marginTop: '4px' }}>
                                    Follow-up: {followupLabel}
                                </div>
                            )}

                            {kind === 'scheduled' && isCompleted && (
                                <div className="crm-timeline-body" style={{ marginTop: '10px' }}>
                                    <select
                                        className="crm-select"
                                        value={activity.outcome || ''}
                                        onChange={(event) => onOutcomeChange?.(activity, event.target.value)}
                                    >
                                        {outcomeOptions.map((opt) => (
                                            <option key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {kind === 'scheduled' && dueEpoch && !isCompleted && (
                                <div
                                    style={{
                                        marginTop: '8px',
                                        fontSize: '12px',
                                        color: dueEpoch < Date.now() ? 'var(--crm-danger)' : 'var(--crm-gray-500)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                    }}
                                >
                                    Due: {new Date(dueEpoch).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                                    {dueEpoch < Date.now() && ' (Overdue)'}
                                </div>
                            )}

                            <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                                {isPending && (
                                    <button
                                        className="crm-btn crm-btn-primary crm-btn-sm"
                                        onClick={() => onPrimaryAction?.(activity, channelKey)}
                                        disabled={primaryDisabled}
                                        title={primaryDisabled ? (channelKey === 'email' ? 'No email on lead' : 'No phone on lead') : ''}
                                    >
                                        {primaryLabel}
                                    </button>
                                )}
                                {isPending && (
                                    <button
                                        className="crm-btn crm-btn-success crm-btn-sm"
                                        onClick={() => onComplete?.(activity)}
                                    >
                                        Mark Done
                                    </button>
                                )}
                                {kind === 'log' && (
                                    <button
                                        className="crm-btn crm-btn-secondary crm-btn-sm"
                                        onClick={() => onEdit?.(activity)}
                                    >
                                        Edit
                                    </button>
                                )}
                                {kind === 'scheduled' && isPending && (
                                    <button
                                        className="crm-btn crm-btn-secondary crm-btn-sm"
                                        onClick={() => onReschedule?.(activity)}
                                    >
                                        Edit
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

export default ActivityTimeline;
